/**
 * Runtime Trace Viewer (S13 / P2 #21 — the "why did / why did NOT this happen"
 * inspectability surface, screens/10-agent-trace-viewer).
 *
 * Renders the full runtime chain behind one agent run:
 *
 *   Event → RuleRun → EffectRun(s) → AgentRun
 *
 * so an operator can see what fired, what each effect did, and — critically —
 * which effects were *policy_blocked* (a skipped effect carrying a block reason),
 * which is the "why did this NOT happen" half of the promise. The data already
 * exists from S5/S6: every node is read from an existing list/show endpoint.
 *
 * Lens/projection doctrine: a READ projection of authorized data. It renders the
 * trace; it creates no effects, mints no credentials, and changes no truth. The
 * one mutating affordance (retry) lives on the existing AgentRun show/trace and
 * is intentionally NOT duplicated here — this surface is read-only.
 *
 * All four reads (agent-runs, rule-runs, effect-runs, events) go through the
 * existing CRUD endpoints, including the `?rule_run_id=` equality filter the
 * list factory already supports.
 */
import * as React from "react";
import { useParams } from "react-router-dom";

import type { AgentRun, EffectRun, Event, RuleRun } from "@aos/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Icon, type IconName } from "@/components/ui/icon";
import { Separator } from "@/components/ui/separator";
import { RenderState } from "@/components/app/RenderState";
import { StatusBadge } from "@/components/app/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import { Field, FieldGrid, InspectorSection, RefLink } from "@/components/inspector";
import { ApiError, api } from "@/lib/api";
import { formatDateTime, humanize } from "@/lib/format";

interface ListResponse<T> {
  items: T[];
}

interface TracePacket {
  agentRun: AgentRun;
  ruleRun: RuleRun | null;
  event: Event | null;
  effectRuns: EffectRun[];
}

/** A skipped effect with a recorded reason is the runtime's "policy_blocked". */
function isPolicyBlocked(effect: EffectRun): boolean {
  return effect.status === "skipped" && Boolean(effect.last_error);
}

async function loadTrace(agentRunId: string): Promise<TracePacket> {
  const agentRun = await api.get<AgentRun>(`/agent-runs/${encodeURIComponent(agentRunId)}`);

  let ruleRun: RuleRun | null = null;
  let event: Event | null = null;
  let effectRuns: EffectRun[] = [];

  if (agentRun.rule_run_id) {
    ruleRun = await api
      .get<RuleRun>(`/rule-runs/${encodeURIComponent(agentRun.rule_run_id)}`)
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      });

    const effects = await api
      .get<ListResponse<EffectRun>>("/effect-runs", {
        query: { rule_run_id: agentRun.rule_run_id, pageSize: 200 },
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
          return { items: [] };
        }
        throw err;
      });
    effectRuns = effects.items ?? [];

    if (ruleRun?.event_id) {
      event = await api
        .get<Event>(`/events/${encodeURIComponent(ruleRun.event_id)}`)
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 404) return null;
          throw err;
        });
    }
  }

  return { agentRun, ruleRun, event, effectRuns };
}

function useTrace(agentRunId: string) {
  const [data, setData] = React.useState<TracePacket | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const reload = React.useCallback(() => setReloadKey((v) => v + 1), []);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadTrace(agentRunId)
      .then((packet) => {
        if (!cancelled) setData(packet);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load trace.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agentRunId, reloadKey]);

  return { data, loading, error, reload };
}

/** One node in the vertical Event → Rule → Effects → Agent chain. */
function TraceNode({
  icon,
  kind,
  title,
  status,
  children,
  blocked,
}: {
  icon: IconName;
  kind: string;
  title: React.ReactNode;
  status?: string | null;
  children?: React.ReactNode;
  blocked?: boolean;
}) {
  return (
    <Card data-testid={`trace-node-${kind}`} className={blocked ? "border-destructive/40" : undefined}>
      <CardHeader className="p-4 pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-2">
            <Icon name={icon} className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{kind}</p>
              <CardTitle className="text-sm [overflow-wrap:anywhere]">{title}</CardTitle>
            </div>
          </div>
          {status ? <StatusBadge status={status} /> : null}
        </div>
      </CardHeader>
      {children ? <CardContent className="p-4 pt-0">{children}</CardContent> : null}
    </Card>
  );
}

/** A thin connector glyph between two trace nodes. */
function TraceArrow() {
  return (
    <div className="flex justify-center py-1 text-muted-foreground" aria-hidden>
      <Icon name="sortDesc" />
    </div>
  );
}

function TraceContent({ packet, reload }: { packet: TracePacket; reload: () => void }) {
  const { agentRun, ruleRun, event, effectRuns } = packet;
  const blockedCount = effectRuns.filter(isPolicyBlocked).length;

  return (
    <div className="flex flex-col gap-6">
      <InspectorSection
        title="Run summary"
        aside={
          <Button variant="outline" size="sm" onClick={reload}>
            <Icon name="reopen" data-icon="inline-start" aria-hidden />
            Refresh
          </Button>
        }
      >
        <FieldGrid>
          <Field label="Agent run">
            <span className="font-mono text-xs">{agentRun.id}</span>
          </Field>
          <Field label="Status">
            <StatusBadge status={agentRun.status} />
          </Field>
          <Field label="Executor">{humanize(agentRun.executor_type)}</Field>
          <Field label="Started">{formatDateTime(agentRun.started_at ?? null)}</Field>
          <Field label="Rule run">
            <RefLink resource="rule-runs" id={agentRun.rule_run_id} />
          </Field>
          <Field label="Effects blocked">
            {blockedCount > 0 ? (
              <Badge variant="destructive">
                <Icon name="reject" aria-hidden />
                {blockedCount} policy-blocked
              </Badge>
            ) : (
              "0"
            )}
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Trace chain">
        <div className="flex flex-col">
          {event ? (
            <TraceNode
              icon="events"
              kind="Event"
              title={
                <RefLink resource="events" id={event.id} label={humanize(event.event_type)} />
              }
              status={event.status}
            >
              <FieldGrid>
                <Field label="Source">{event.source}</Field>
                <Field label="Replayable">{event.replayable ? "Yes" : "No"}</Field>
              </FieldGrid>
            </TraceNode>
          ) : (
            <TraceNode
              icon="events"
              kind="Event"
              title={<span className="text-muted-foreground">No source event recorded</span>}
            />
          )}

          <TraceArrow />

          {ruleRun ? (
            <TraceNode
              icon="ruleRuns"
              kind="Rule run"
              title={<RefLink resource="rule-runs" id={ruleRun.id} />}
              status={ruleRun.status}
            >
              <FieldGrid>
                <Field label="Rule">
                  <RefLink resource="rules" id={ruleRun.rule_id} />
                </Field>
                <Field label="Version">{ruleRun.rule_version}</Field>
                {ruleRun.error ? (
                  <Field label="Error" full>
                    <span className="text-destructive">{ruleRun.error}</span>
                  </Field>
                ) : null}
              </FieldGrid>
            </TraceNode>
          ) : (
            <TraceNode
              icon="ruleRuns"
              kind="Rule run"
              title={
                <span className="text-muted-foreground">
                  Run not dispatched by a rule (manual / direct activation)
                </span>
              }
            />
          )}

          <TraceArrow />

          <TraceNode icon="effectRuns" kind={`Effects (${effectRuns.length})`} title="Effects fired by the rule">
            {effectRuns.length === 0 ? (
              <p className="text-sm text-muted-foreground">No effect runs recorded.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {effectRuns.map((effect) => {
                  const blocked = isPolicyBlocked(effect);
                  return (
                    <div
                      key={effect.id}
                      data-testid={blocked ? "effect-policy-blocked" : "effect-run"}
                      className="rounded-md border border-border px-3 py-2"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-sm font-medium">{humanize(effect.effect_type)}</span>
                        <div className="flex items-center gap-2">
                          {blocked ? (
                            <Badge variant="destructive">
                              <Icon name="reject" aria-hidden />
                              Policy blocked
                            </Badge>
                          ) : null}
                          <StatusBadge status={effect.status} />
                        </div>
                      </div>
                      {effect.target_type ? (
                        <p className="mt-1 font-mono text-xs text-muted-foreground">
                          {effect.target_type}:{effect.target_id ?? "—"}
                        </p>
                      ) : null}
                      {effect.last_error ? (
                        <p className="mt-1 text-xs text-destructive [overflow-wrap:anywhere]">
                          {effect.last_error}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </TraceNode>

          <TraceArrow />

          <TraceNode
            icon="agentRuns"
            kind="Agent run"
            title={<span className="font-mono text-xs">{agentRun.id}</span>}
            status={agentRun.status}
          >
            <FieldGrid>
              <Field label="Output summary" full>
                {agentRun.output_summary ?? "—"}
              </Field>
              {agentRun.error ? (
                <Field label="Error" full>
                  <span className="text-destructive">{agentRun.error}</span>
                </Field>
              ) : null}
            </FieldGrid>
            <Separator className="my-3" />
            <div className="flex flex-wrap gap-2 text-xs">
              <Badge variant="outline">Events {agentRun.produced_event_count}</Badge>
              <Badge variant="outline">Claims {agentRun.produced_claim_count}</Badge>
              <Badge variant="outline">Evidence {agentRun.produced_evidence_count}</Badge>
              <Badge variant="outline">Artifacts {agentRun.produced_artifact_count}</Badge>
            </div>
          </TraceNode>
        </div>
      </InspectorSection>
    </div>
  );
}

export function TraceViewerRoute() {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    return <RenderState state="error" errorMessage="Missing agent run id." />;
  }
  return <TraceViewerPage agentRunId={id} />;
}

function TraceViewerPage({ agentRunId }: { agentRunId: string }) {
  const { data, loading, error, reload } = useTrace(agentRunId);

  return (
    <div className="max-w-5xl">
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Runtime Trace Viewer"
          subtitle="The full runtime chain behind one agent run — event, rule, effects (including policy-blocked outcomes), and output. Read-only."
          icon="agentRuns"
        />
        {loading ? (
          <RenderState state="loading" loadingRows={6} />
        ) : error ? (
          <RenderState state="error" errorMessage={error} onRetry={reload} />
        ) : data ? (
          <TraceContent packet={data} reload={reload} />
        ) : (
          <RenderState
            state="empty"
            empty={{ icon: "agentRuns", title: "No trace for this run." }}
          />
        )}
      </div>
    </div>
  );
}

export default TraceViewerRoute;
